import { ReactNode, useState } from 'react';
import { FormControl, SxProps } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { ClearIcon } from './ClearIcon';
import FieldLabel from '../../FieldLabel';
import { DateTime } from 'luxon';
import { humanizeDate } from '../../../helpers/humanizeDate';
import { designSystemElevations } from '../../../../appTheme/appTheme';

export interface DateSelectorProps {
  initialValue?: string; //in SQL format
  onChange: (value: string) => void;
  shouldBeDisabled?: boolean;
  textFieldSx?: SxProps;
  placeholder?: string;
  label?: string;
  infoTooltipTitle?: NonNullable<ReactNode>;
  showTooltip?: boolean;
  id?: string;
  removeBorders?: boolean;
  isRequired?: boolean;
}

const DateSelector = ({
  initialValue,
  onChange,
  shouldBeDisabled,
  textFieldSx,
  placeholder,
  label,
  id,
  showTooltip,
  infoTooltipTitle,
  removeBorders,
  isRequired,
}: DateSelectorProps) => {
  const [newValue, setValue] = useState(() => {
    if (!initialValue) return undefined;
    // Parse the initial value in UTC
    return DateTime.fromISO(initialValue, { zone: 'UTC' });
  });

  const handleSetValue = (value?: DateTime | null) => {
    if (!value) {
      setValue(undefined);
      onChange('');
      return;
    }

    // Convert to UTC before storing
    const utcDate = value.toUTC();
    const parsedVal = utcDate.toSQLDate() ?? '';
    setValue(utcDate);
    onChange(parsedVal);
  };

  return (
    <FormControl fullWidth>
      {label && (
        <FieldLabel
          label={label}
          id={id}
          infoTooltipTitle={infoTooltipTitle}
          showTooltip={showTooltip}
          markLabelAsRequired={isRequired}
        />
      )}
      <DatePicker
        disabled={shouldBeDisabled}
        value={newValue}
        defaultValue={newValue}
        onChange={handleSetValue}
        sx={{
          fieldset: {
            border: removeBorders ? 'none' : 'auto',
          },
        }}
        slotProps={{
          layout: {
            sx: {
              boxShadow: designSystemElevations.Elevation7,
              borderRadius: 2,
            },
          },
          textField: (params) => ({
            disabled: shouldBeDisabled,
            inputProps: {
              ...params.inputProps,
              placeholder: placeholder ?? 'Choose date',
              disabled: true,
              value: humanizeDate({
                dateOrTimestamp: newValue?.toSQLDate() ?? '',
                timeZone: 'UTC', // Always display in UTC
              }),
            },
            sx: { ...params.sx, ...textFieldSx },
            error: false,
            InputProps: {
              endAdornment: (
                <>
                  {newValue && !shouldBeDisabled && (
                    <ClearIcon clearValue={() => handleSetValue(undefined)} />
                  )}
                  {params.InputProps?.endAdornment}
                </>
              ),
            },
          }),
        }}
      />
    </FormControl>
  );
};

export { DateSelector };
